import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore, type Theme } from '@/stores/uiStore';
import { useLanguage } from '@/context/LanguageContext';

/**
 * Restore the signed-in user's server-persisted appearance preferences
 * (theme + language) into the local UI stores.
 *
 * Runs once per user id after `fetchUser()` resolves, so:
 * - Preferences survive page refresh AND logout/login (server is source of truth).
 * - A fresh selection made on the Appearance page is never clobbered by a
 *   later re-render, because we only apply when the user id changes.
 *
 * Must be rendered inside <LanguageProvider>.
 */
export function useUserAppearanceSync() {
    const user = useAuthStore((state) => state.user);
    const setTheme = useUIStore((state) => state.setTheme);
    const { setLanguage } = useLanguage();
    const appliedForUserId = useRef<number | null>(null);

    useEffect(() => {
        if (!user || user.id === appliedForUserId.current) {
            return;
        }
        appliedForUserId.current = user.id;

        // Map the server theme to the UI store.
        // 'auto' is now a first-class value meaning time-based switching.
        const serverTheme = user.theme;
        if (serverTheme === 'light' || serverTheme === 'dark' || serverTheme === 'system' || serverTheme === 'auto') {
            setTheme(serverTheme as Theme);
        }

        if (user.language) {
            setLanguage(user.language);
        }
    }, [user, setTheme, setLanguage]);
}
